import { format } from 'date-fns';

/**
 * Process time series data for export
 * @param {Object} options - Options
 * @param {Array} options.data - Raw data
 * @param {string} options.type - Chart type
 * @param {string} [options.unit] - Unit of measurement
 * @param {any} [options.metadata] - Additional metadata for specific processors
 * @returns {Object} Processed data and headers
 */
export const processChartData = ({ data, type, unit, metadata }) => {
  if (!data?.length) return { data: [], headers: [] };

  const processors = {
    default: processDefaultChart,
    energy: processEnergyChart,
    eos: processEnergyChart,
    temperature: processTemperatureChart,
    'three-phase': processThreePhaseChart,
    'ic meter': processIcMeterChart,
    electricity: processElectricityData,
    'stacked-energy': processStackedEnergyData,
    line: processDefaultChart,
  };

  const processor = processors[type?.toLowerCase()] || processors.default;

  if (type?.toLowerCase() === 'stacked-energy') {
    return processor(data, metadata);
  }

  return processor(data, unit);
};

/**
 * Process default chart data
 * @param {Array} data - Raw data
 * @param {string} unit - Unit of measurement
 * @returns {Object} Processed data and headers
 */
function processDefaultChart(data, unit) {
  const processedData = data.map((item) => ({
    timestamp: format(
      new Date(item.date || item.timestamp),
      'yyyy-MM-dd HH:mm:ss'
    ),
    value: Number(item.average_value || 0),
    min: Number(item.min_value || 0),
    max: Number(item.max_value || 0),
    unit: unit || '',
  }));

  processedData.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  return {
    data: processedData,
    headers: Object.keys(processedData[0] || {}),
  };
}

/**
 * Process energy chart data
 * @param {Array} data - Raw data
 * @param {string} unit - Unit of measurement (default: kWh)
 * @returns {Object} Processed data and headers
 */
function processEnergyChart(data, unit = 'kWh') {
  const processedData = data.map((item) => ({
    timestamp: format(
      new Date(item.date || item.timestamp),
      'yyyy-MM-dd HH:mm:ss'
    ),
    maxValue: Number(item.max_value ?? item.average_value ?? 0),
    unit: unit || 'kWh',
  }));

  processedData.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  let headers = [];
  if (processedData.length > 0) {
    headers = Object.keys(processedData[0]).map((key) => {
      if (key === 'maxValue' && unit) {
        return `${key} (${unit})`;
      }
      return key;
    });
  }

  return {
    data: processedData,
    headers: headers,
  };
}

/**
 * Process temperature chart data
 * @param {Array} data - Raw data
 * @param {string} unit - Unit of measurement (default: °C)
 * @returns {Object} Processed data and headers
 */
function processTemperatureChart(data, unit = '°C') {
  const processedData = data.map((item) => ({
    timestamp: format(
      new Date(item.date || item.timestamp),
      'yyyy-MM-dd HH:mm:ss'
    ),
    temperature: Number(item.average_value || 0),
    unit: unit || '°C',
  }));

  processedData.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  const headers = ['timestamp', 'temperature (°C)', 'unit'];
  return {
    data: processedData,
    headers,
  };
}

/**
 * Process three-phase chart data
 * @param {Array} data - Raw data
 * @param {string} unit - Unit of measurement
 * @returns {Object} Processed data and headers
 */
function processThreePhaseChart(data, unit) {
  const processedData = data.map((item) => ({
    timestamp: format(
      new Date(item.date || item.timestamp),
      'yyyy-MM-dd HH:mm:ss'
    ),
    phase1: Number(item.phase1 || item.average_value || 0),
    phase2: Number(item.phase2 || 0),
    phase3: Number(item.phase3 || 0),
    unit: unit || '',
  }));

  processedData.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  return {
    data: processedData,
    headers: Object.keys(processedData[0] || {}),
  };
}

/**
 * Process IC meter chart data
 * @param {Array} data - Raw data
 * @returns {Object} Processed data and headers with units
 */
function processIcMeterChart(data) {
  const processedData = data.map((item) => {
    try {
      const parsed =
        typeof item.non_numeric_data === 'string'
          ? JSON.parse(item.non_numeric_data)
          : item.non_numeric_data;

      const row = {
        timestamp: format(
          new Date(item.timestamp || item.date),
          'yyyy-MM-dd HH:mm:ss'
        ),
      };

      if (parsed && typeof parsed === 'object') {
        const commonFields = [
          'temperature',
          'co2',
          'humidity',
          'light',
          'noise',
        ];
        commonFields.forEach((field) => {
          if (parsed[field] !== undefined && parsed[field] !== null) {
            try {
              row[field] = Number(parsed[field]);
            } catch (err) {
              row[field] = parsed[field];
            }
          }
        });

        const skipProperties = [
          'building_name',
          'box_id',
          'unit_name',
          'unit_id',
          'timestamputc',
        ];
        Object.keys(parsed).forEach((key) => {
          const lowerKey = key.toLowerCase();
          if (
            !commonFields.includes(lowerKey) &&
            !skipProperties.includes(lowerKey) &&
            !row[lowerKey] &&
            parsed[key] !== null &&
            parsed[key] !== undefined
          ) {
            try {
              row[lowerKey] = Number(parsed[key]);
            } catch (err) {
              row[lowerKey] = parsed[key];
            }
          }
        });
      }

      return row;
    } catch (error) {
      console.error('Error parsing IC-meter data:', error);
      return {
        timestamp: format(
          new Date(item.timestamp || item.date),
          'yyyy-MM-dd HH:mm:ss'
        ),
        error: 'Failed to parse data',
      };
    }
  });

  processedData.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  const UNIT_MAPPING = {
    co2: 'ppm',
    temperature: 'C',
    humidity: '%',
    light: 'lux',
    noise: 'dB',
  };

  let headers = [];
  if (processedData.length > 0) {
    headers = Object.keys(processedData[0]).map((key) => {
      const unit = UNIT_MAPPING[key.toLowerCase()];
      return unit ? `${key} (${unit})` : key;
    });
  }

  return {
    data: processedData,
    headers,
  };
}

/**
 * Process electricity chart data
 * @param {Array} data - Raw electricity data with timestamps
 * @param {string} unit - Unit of measurement (default: kWh)
 * @returns {Object} Processed data and headers
 */
function processElectricityData(data, unit = 'kWh') {
  if (!data || data.length === 0) return { data: [], headers: [] };

  const processedData = data.map((item) => ({
    timestamp: format(item.ts, 'yyyy-MM-dd HH:mm:ss'),
    consumption: item.value,
    unit: unit || 'kWh',
  }));

  processedData.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  return {
    data: processedData,
    headers: Object.keys(processedData[0] || {}),
  };
}

function processStackedEnergyData(data, metadata) {
  if (!data || data.length === 0) return { data: [], headers: [] };

  const labels = metadata?.labels ?? {};
  const timestampLabel = labels.timestamp ?? 'Timestamp';
  const electricityLabel = labels.electricity ?? 'Electricity';
  const districtHeatingLabel = labels.districtHeating ?? 'District Heating';
  const solarPvLabel = labels.solarPv ?? 'Solar PV';
  const exportedElectricityLabel =
    labels.exportedElectricity ?? 'Exported Electricity';
  const totalLabel = labels.total ?? 'Total';

  const hasElectricity = data.some((item) => item.activeValue != null);
  const hasDistrictHeating = data.some(
    (item) => item.districtHeatingValue != null
  );
  const hasSolarPv = data.some((item) => item.solarPvValue != null);
  const hasExportedElectricity = data.some(
    (item) => item.electricityExportedValue != null
  );

  const sourceCount = [
    hasElectricity,
    hasDistrictHeating,
    hasSolarPv,
    hasExportedElectricity,
  ].filter(Boolean).length;
  const includeTotal = sourceCount > 1;

  const processedData = data
    .filter(
      (item) =>
        item.activeValue != null ||
        item.districtHeatingValue != null ||
        item.solarPvValue != null ||
        item.electricityExportedValue != null
    )
    .map((item) => {
      const row = {
        [timestampLabel]: format(item.ts, 'yyyy-MM-dd HH:mm:ss'),
      };
      let total = 0;
      let hasAny = false;

      if (hasElectricity) {
        const value =
          item.activeValue != null ? Number(item.activeValue) : null;
        row[electricityLabel] = value;
        if (value !== null) {
          total += value;
          hasAny = true;
        }
      }
      if (hasDistrictHeating) {
        const value =
          item.districtHeatingValue != null
            ? Number(item.districtHeatingValue)
            : null;
        row[districtHeatingLabel] = value;
        if (value !== null) {
          total += value;
          hasAny = true;
        }
      }
      if (hasSolarPv) {
        const value =
          item.solarPvValue != null ? Number(item.solarPvValue) : null;
        row[solarPvLabel] = value;
        if (value !== null) {
          total += value;
          hasAny = true;
        }
      }

      // consumed + produced - exported
      if (hasExportedElectricity) {
        const value =
          item.electricityExportedValue != null
            ? Number(item.electricityExportedValue)
            : null;
        row[exportedElectricityLabel] = value;
        if (value !== null) {
          total -= value;
          hasAny = true;
        }
      }

      if (includeTotal) {
        row[totalLabel] = hasAny ? Number(total.toFixed(3)) : null;
      }
      return row;
    });

  const headers = [timestampLabel];
  if (hasElectricity) headers.push(`${electricityLabel} (kWh)`);
  if (hasDistrictHeating) headers.push(`${districtHeatingLabel} (kWh)`);
  if (hasSolarPv) headers.push(`${solarPvLabel} (kWh)`);
  if (hasExportedElectricity) headers.push(`${exportedElectricityLabel} (kWh)`);
  if (includeTotal) headers.push(`${totalLabel} (kWh)`);

  return {
    data: processedData,
    headers,
  };
}

/**
 * Detect chart type from data
 * @param {Array} data - Raw data
 * @param {Array} topics - Device topics
 * @returns {string} Detected chart type
 */
export const detectChartType = (data, topics) => {
  if (data?.[0]?.suggested_type) {
    return data[0].suggested_type;
  }

  if (data?.[0]?.value?.sensorTemperature) {
    return 'combination';
  }

  if (data?.[0]?.value?.box_id) {
    return 'ic meter';
  }

  if (topics?.[0]?.type) {
    return topics[0].type;
  }

  return 'line';
};
